import { Injectable } from "@angular/core"
import { Observable, map } from "rxjs"
import { environment } from "../../environments/environment"
import { ApiService } from "./api.service"
import { normalizeOrderItem } from "../utils/order-display-amount"

export interface OrderItem {
  id: number
  totalairings: number
  startdate: string
  enddate: string
  totalimpressions: number
  totalcost: number
  overall_cpm?: number
  stationid?: number
  station_rate_id?: number
  userid?: number
  promocode?: string | null
  promo_code?: string | null
  payment_intent_id?: string | null
  /** From API when backend stores actual charge */
  amount_paid?: number
  /** Pre-discount total when promo applied */
  original_amount?: number
  discount_amount?: number
  created_at?: string
  updated_at?: string
  station_name?: string
  call_sign?: string
  logo_url?: string
  user_email?: string
  user_name?: string
  username?: string
  user_firstname?: string
  user_lastname?: string
  user_accounttype?: string
  user_businessname?: string
  /** Shared by every order created in one cart checkout. Absent on single orders. */
  checkout_transaction_id?: string | null
  /** How many orders share checkout_transaction_id, when the list response includes it. */
  checkout_group_size?: number
  /** True when the API can serve one receipt for the whole grouped purchase. */
  combined_receipt_available?: boolean
  combined_receipt_url?: string | null
}

export interface OrdersResponse {
  status: string
  orders: OrderItem[]
  pagination?: {
    total: number
    limit: number
    offset: number
    has_more: boolean
  }
  orderEligibility?: {
    hasRecordings: boolean
    hasApprovedRecording: boolean
    canCreateOrder: boolean
  }
}

@Injectable({ providedIn: "root" })
export class OrdersService {
  private readonly ORDERS_URL = `${environment.apiUrl}/orders`

  constructor(private api: ApiService) {}

  /**
   * List orders scoped by the JWT role on the backend.
   * Do not pass userid / user_id — the API derives visibility from the token.
   */
  list(params?: {
    limit?: number
    offset?: number
    order?: string
    owner_id?: number
    station_id?: number
  }): Observable<OrdersResponse> {
    let url = this.ORDERS_URL
    if (params) {
      const parts: string[] = []
      if (params.limit !== undefined) parts.push(`limit=${params.limit}`)
      if (params.offset !== undefined) parts.push(`offset=${params.offset}`)
      if (params.order !== undefined) parts.push(`order=${params.order}`)
      if (params.owner_id !== undefined) parts.push(`owner_id=${params.owner_id}`)
      if (params.station_id !== undefined) parts.push(`station_id=${params.station_id}`)
      if (parts.length > 0) {
        url += "?" + parts.join("&")
      }
    }
    // Using authenticated GET to include bearer if present
    return this.api.authenticatedGet<OrdersResponse>(url).pipe(
      map((res) => ({
        ...res,
        orders: (res.orders || []).map((o) =>
          normalizeOrderItem(o as unknown as Record<string, unknown>),
        ),
      })),
    )
  }
}
